// Series finder: the rest of this product's series, filtered by product type.
//
// Why there is a fetch here at all. Liquid on a product page can read this
// product's `kludi.series`, but it cannot ask the catalogue which other
// products carry the same value -- there is no per-series collection to walk
// (collections in this catalogue are flat and their hierarchy lives only in
// the main menu) and no Liquid filter that queries by metafield. What the
// store does have is a live storefront filter on that metafield, so the
// question is asked the only way it can be: the all-products collection URL
// with `filter.p.m.kludi.series` applied, rendered through the Section
// Rendering API by kludi-series-finder-results.liquid. One request, every
// product in the series, and the type tabs derived from that same set.
//
// The band stays hidden unless the answer is worth showing. A failed request,
// an empty one, or a series that holds nothing but the product already on
// screen all end the same way: nothing is revealed and the page reads as if
// the section were not there. That is deliberate -- the alternative is a
// full-height sand band that flashes empty on every product page while the
// request is in flight.
//
// Scrolling, overflow detection and the mobile position track are
// kludi-rail.js, this storefront's one rail controller. What this class adds
// is the type tabs, the "show all" cap, and the fetch above.
class KludiSeriesFinder extends window.KludiRail {
  connectedCallback() {
    this.section = this.closest('.kludi-series-finder');
    this.tabList = this.querySelector('[data-kludi-finder-tabs]');
    this.grid = this.querySelector('[data-kludi-finder-grid]');
    this.activeType = '';
    this.tabs = [];


    this.initRail();
    this.load();
  }

  get items() {
    return Array.from(this.grid?.querySelectorAll('[data-kludi-finder-item]') || []);
  }

  async load() {
    const endpoint = this.dataset.endpoint;
    if (!endpoint || !this.grid || !this.tabList) return;

    let payload;
    try {
      const response = await fetch(endpoint);
      if (!response.ok) return;
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      payload = parsed.querySelector('[data-kludi-finder-payload]');
    } catch (error) {
      // a finder that cannot answer says nothing; the page is complete without it
      return;
    }

    if (!payload) return;

    const items = payload.querySelectorAll('[data-kludi-finder-item]');

    // one product is this product: a band headed "everything in the series"
    // that then shows the page you are already on is noise, not navigation
    if (items.length < 2) return;

    this.tabList.replaceChildren(...payload.querySelectorAll('[data-kludi-finder-tab]'));
    this.grid.replaceChildren(...items);

    this.tabs = Array.from(this.tabList.querySelectorAll('[data-kludi-finder-tab]'));
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => this._onTabKeydown(event, tab));
    });

    // a series that only ever produced one type has nothing to filter by, so
    // its lone "All" tab is a control that cannot do anything
    if (this.tabs.length < 2) this.tabList.hidden = true;

    if (this.section) this.section.hidden = false;
    this.render();
  }

  select(tab) {
    if (!this.tabs.includes(tab)) return;

    this.activeType = tab.dataset.type || '';

    // a new selection is read from its beginning rather than from wherever
    // the previous one had been scrolled to
    this.tabs.forEach((candidate) => {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', selected ? 'true' : 'false');
      candidate.tabIndex = selected ? 0 : -1;
    });

    this.render();

    // 'auto' rather than the row's own smooth behaviour: the rail is being
    // replaced, not travelled through
    this.grid?.scrollTo({ left: 0, behavior: 'auto' });
  }

  render() {
    /*
      every matching product is in the rail. the band used to show four and
      hide the rest behind a "show all" button, which made sense while this
      was a fixed four-column grid -- as a scroller it did not: the control
      offered to reveal products the rail could simply have been scrolled to,
      and the count in its label contradicted what was on screen
    */
    const matching = this.items.filter((item) => !this.activeType || item.dataset.type === this.activeType);

    this.items.forEach((item) => {
      item.hidden = !matching.includes(item);
    });

    this.refreshRail();
  }

  // roving tabindex: the tab row is one stop in the tab order and the arrow
  // keys move between the tabs inside it
  _onTabKeydown(event, tab) {
    const step = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' }[event.key];
    if (step === undefined) return;

    event.preventDefault();
    const index = this.tabs.indexOf(tab);
    let target;
    if (step === 'first') target = this.tabs[0];
    else if (step === 'last') target = this.tabs[this.tabs.length - 1];
    else target = this.tabs[(index + step + this.tabs.length) % this.tabs.length];

    target.focus();
    this.select(target);
  }
}

window.customElements.define('kludi-series-finder', KludiSeriesFinder);
